import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LogoIcon, LogoFull } from "./brand/Logo";
import { AuthContext } from "../context/AuthContext";
import {
  Home,
  Grid,
  Users,
  FileText,
  CreditCard,
  Users as UsersIcon,
  Bell,
  LogOut,
  Menu,
  ChevronDown,
  Droplets,
  Gauge,
  Zap,
  Settings,
  ShieldCheck,
  Mail,
  Smartphone,
  Coins,
  BarChart3,
  Activity,
  LayoutDashboard,
  Shield,
  Package,
  Landmark,
  BookOpen,
  Wallet,
  PiggyBank,
  GitMerge,
  Building2
} from "lucide-react";
import { useContext } from "react";
import { usePermissions } from "../hooks/usePermissions";

const NoCashIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect
      x="4"
      y="6"
      width="6"
      height="13"
      rx="3"
      transform="rotate(20 7 12.5)"
      fill="#0d9488"
    />
    <rect
      x="12"
      y="4"
      width="6"
      height="13"
      rx="3"
      transform="rotate(20 15 10.5)"
      fill="#06b6d4"
    />
  </svg>
);

interface SidebarProps {
  isOpen?: boolean;
  onToggle?: () => void;
  onLogout?: () => void;
  collapsed?: boolean;
}

interface MenuItem {
  name: string;
  to: string;
  icon: React.ReactNode;
  permissionModule?: string; // Hide unless user has READ on this module (or is admin)
  adminOnly?: boolean;       // Hide unless user is TENANT_ADMIN or SUPER_ADMIN
}

interface MenuGroup {
  groupName: string;
  items: MenuItem[];
  roles?: string[]; // Optional: restricted to these roles
}

const menuGroups: MenuGroup[] = [
  {
    groupName: "Platform Admin",
    roles: ["SUPER_ADMIN"],
    items: [
      { name: "Revenue Dashboard", to: "/dashboard/platform-revenue", icon: <LayoutDashboard className="w-4 h-4" /> },
    ],
  },
  {
    groupName: "Core",
    items: [
      { name: "Overview", to: "/dashboard", icon: <Home className="w-4 h-4" /> },
    ],
  },
  {
    groupName: "Property & People",
    items: [
      { name: "Blocks",      to: "/dashboard/blocks",     icon: <Grid className="w-4 h-4" />, permissionModule: "RESIDENTS" },
      { name: "Flats / Units", to: "/dashboard/flats",   icon: <Home className="w-4 h-4" />, permissionModule: "RESIDENTS" },
      { name: "Flat Owners", to: "/dashboard/owners",     icon: <UsersIcon className="w-4 h-4" />, permissionModule: "RESIDENTS" },
      { name: "Residents",   to: "/dashboard/residents",  icon: <Users className="w-4 h-4" />, permissionModule: "RESIDENTS" },
      { name: "Staff",       to: "/dashboard/staff",      icon: <UsersIcon className="w-4 h-4" />, permissionModule: "RESIDENTS" },
    ],
  },
  {
    groupName: "Utilities",
    items: [
      { name: "Suppliers",          to: "/dashboard/suppliers",          icon: <UsersIcon className="w-4 h-4" />, permissionModule: "SUPPLIERS" },
      { name: "Water Purchases",    to: "/dashboard/water-purchases",    icon: <Droplets className="w-4 h-4" />, permissionModule: "WATER" },
      { name: "Water Consumption",  to: "/dashboard/water-consumption",  icon: <Gauge className="w-4 h-4" />,   permissionModule: "WATER" },
      { name: "Water Billing",      to: "/dashboard/water-billing",      icon: <FileText className="w-4 h-4" />, permissionModule: "WATER" },
      { name: "Electricity Bills",  to: "/dashboard/electricity-bills",  icon: <Zap className="w-4 h-4" />,     permissionModule: "ELECTRICITY" },
    ],
  },
  {
    groupName: "Finance & Ops",
    items: [
      { name: "Expenditure",     to: "/dashboard/expenditures",    icon: <FileText className="w-4 h-4" />,  permissionModule: "EXPENDITURE" },
      // Outstanding Write-off: release-gated (REACT_APP_ENABLE_OUTSTANDING_WRITEOFF=true).
      ...(process.env.REACT_APP_ENABLE_OUTSTANDING_WRITEOFF === "true"
        ? [{ name: "Outstanding Write-off", to: "/dashboard/outstanding-writeoff", icon: <GitMerge className="w-4 h-4" />, adminOnly: true }]
        : []),
      { name: "Opening Balances", to: "/dashboard/opening-balances", icon: <Coins className="w-4 h-4" />, adminOnly: true },
      { name: "Monthly Billing", to: "/dashboard/billing-summary", icon: <FileText className="w-4 h-4" />,  permissionModule: "BILLING" },
      { name: "Receipts",         to: "/dashboard/payments",        icon: <CreditCard className="w-4 h-4" />, permissionModule: "PAYMENTS" },
      { name: "Notices",         to: "/dashboard/notices",         icon: <Bell className="w-4 h-4" />,       permissionModule: "NOTICES" },
    ],
  },
  {
    groupName: "Accounts & Funds",
    items: [
      { name: "Financial Position",  to: "/dashboard/treasury",                    icon: <Landmark className="w-4 h-4" />,   permissionModule: "TREASURY" },
      { name: "Balance Sheet",       to: "/dashboard/treasury/balance-sheet",      icon: <BarChart3 className="w-4 h-4" />,  permissionModule: "TREASURY" },
      { name: "Bank Accounts",      to: "/dashboard/treasury/bank-accounts",      icon: <Building2 className="w-4 h-4" />, permissionModule: "TREASURY" },
      { name: "Cash Account",       to: "/dashboard/treasury/cash-in-hand",       icon: <Wallet className="w-4 h-4" />,    permissionModule: "TREASURY" },
      { name: "Funds",              to: "/dashboard/treasury/funds",              icon: <PiggyBank className="w-4 h-4" />, permissionModule: "TREASURY" },
      { name: "Cash Flow",           to: "/dashboard/treasury/cashbook",          icon: <BookOpen className="w-4 h-4" />,  permissionModule: "TREASURY" },
      { name: "Account Passbook",   to: "/dashboard/treasury/account-statements",  icon: <FileText className="w-4 h-4" />,  permissionModule: "TREASURY" },
      { name: "Verification Report", to: "/dashboard/treasury/verification-report", icon: <FileText className="w-4 h-4" />,  permissionModule: "TREASURY" },
      { name: "Bank Reconciliation", to: "/dashboard/treasury/bank-reconciliation", icon: <GitMerge className="w-4 h-4" />,  permissionModule: "TREASURY" },
    ],
  },
  {
    groupName: "Settings",
    items: [
      { name: "Data Migration",      to: "/dashboard/smart-import",      icon: <ShieldCheck className="w-4 h-4 text-blue-600" />, permissionModule: "MIGRATIONS" },
      { name: "Billing Rules",         to: "/dashboard/billing-rules",             icon: <Settings className="w-4 h-4" />,                  permissionModule: "BILLING" },
      { name: "Email Settings",        to: "/dashboard/email-config",              icon: <Mail className="w-4 h-4" />,                      adminOnly: true },
      { name: "Subscriptions",          to: "/dashboard/whatsapp-alerts",           icon: <Package className="w-4 h-4 text-indigo-500" />,   permissionModule: "WHATSAPP_CONFIG" },
      { name: "Notifications",          to: "/dashboard/notification-preferences",   icon: <Bell className="w-4 h-4 text-indigo-500" />,      adminOnly: true },
      { name: "Billing Configuration",  to: "/dashboard/billing-configuration",      icon: <FileText className="w-4 h-4 text-blue-500" />,   adminOnly: true },
      { name: "nocash setup",           to: "/dashboard/payment-activation",   icon: <NoCashIcon className="w-4 h-4" />,                adminOnly: true },
      { name: "User Management",     to: "/dashboard/user-management",      icon: <UsersIcon className="w-4 h-4" />,                 permissionModule: "USER_MANAGEMENT" },
      { name: "Roles & Permissions", to: "/dashboard/roles-permissions",    icon: <Shield className="w-4 h-4" />,                   adminOnly: true },
    ],
  },
  {
    groupName: "Reports",
    items: [
      { name: "Ledger Report",      to: "/dashboard/reports/ledger",          icon: <FileText className="w-4 h-4" />, permissionModule: "REPORTS" },
      { name: "Financial Health",   to: "/dashboard/reports/financial-health", icon: <Activity className="w-4 h-4" />, permissionModule: "REPORTS" },
      { name: "Budget",             to: "/dashboard/budget",                  icon: <PiggyBank className="w-4 h-4" />, permissionModule: "EXPENDITURE" },
      { name: "Communication Logs", to: "/dashboard/communication-logs",       icon: <Mail className="w-4 h-4" />, permissionModule: "REPORTS" },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  onLogout,
  collapsed = false,
  onToggle,
}) => {
  const { user } = useContext(AuthContext);
  const userRole = user?.role || "NONE";
  const { isAdmin, canRead: canSeeModule } = usePermissions();

  const isItemVisible = (item: MenuItem): boolean => {
    if (item.adminOnly) return isAdmin;
    if (item.permissionModule) return canSeeModule(item.permissionModule);
    return true;
  };

  const location = useLocation();
  const [openSections, setOpenSections] = useState<string[]>(["Core", "Platform Admin"]);

  // Auto-expand sections based on the current route
  useEffect(() => {
    const path = location.pathname;
    const newOpenSections = [...openSections];
    let changed = false;

    if ((path.includes("/blocks") || path.includes("/flats") || path.includes("/owners") || path.includes("/residents") || path.includes("/staff")) && !newOpenSections.includes("Property & People")) {
      newOpenSections.push("Property & People");
      changed = true;
    }
    if ((path.includes("/suppliers") || path.includes("/water") || path.includes("/electricity")) && !newOpenSections.includes("Utilities")) {
      newOpenSections.push("Utilities");
      changed = true;
    }
    if ((path.includes("/expenditure") || path.includes("/billing") || path.includes("/payments") || path.includes("/receipts") || path.includes("/notices")) && !newOpenSections.includes("Finance & Ops")) {
      newOpenSections.push("Finance & Ops");
      changed = true;
    }
    if ((path.includes("/rules") || path.includes("/config") || path.includes("/whatsapp") || path.includes("/smart-import") || path.includes("/roles-permissions") || path.includes("/user-management") || path.includes("/notification-preferences") || path.includes("/billing-configuration")) && !newOpenSections.includes("Settings")) {
      newOpenSections.push("Settings");
      changed = true;
    }
    if ((path.includes("/treasury") || path.includes("/bank-accounts") || path.includes("/cash-in-hand") || path.includes("/cashbook") || path.includes("/account-statements") || path.includes("/verification-report") || path.includes("/bank-reconciliation") || path.includes("/balance-sheet")) && !newOpenSections.includes("Accounts & Funds")) {
      newOpenSections.push("Accounts & Funds");
      changed = true;
    }
    if ((path.includes("/reports") || path.includes("/communication-logs")) && !newOpenSections.includes("Reports")) {
      newOpenSections.push("Reports");
      changed = true;
    }

    if (changed) {
      setOpenSections(newOpenSections);
    }
  }, [location.pathname]);

  const toggleSection = (groupName: string) => {
    if (groupName === "Core") return; // Keep Core always open
    setOpenSections(current => 
      current.includes(groupName) 
        ? current.filter(s => s !== groupName) 
        : [...current, groupName]
    );
  };

  return (
    <aside
      className={`h-screen sticky top-0 ${
        collapsed ? "w-20" : "w-64"
      } bg-surface border-r border-border transition-all duration-300 z-50 flex flex-col font-sans shadow-sm`}
    >
      {/* ───────── Brand Header (Clean White) ───────── */}
      <div className="flex items-center justify-between pl-4 pr-4 py-3.5 min-h-[76px] border-b border-slate-100 transition-all duration-200">
        <NavLink to="/dashboard" className="flex items-center group transition-all duration-200">
          <div className="flex items-center transition-all duration-200">
            {collapsed ? (
              <LogoIcon light={false} className="h-10 w-10 p-1 mx-auto" />
            ) : (
              <LogoFull light={false} size="md" />
            )}
          </div>
        </NavLink>

        <button
          onClick={onToggle}
          aria-label="Toggle sidebar"
          className="p-1.5 rounded-lg hover:bg-surfaceHover text-muted hover:text-text transition-all"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* ───────── Navigation ───────── */}
      <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-6 scrollbar-hide">
        {menuGroups
          .filter(group => !group.roles || group.roles.includes(userRole))
          .filter(group => group.items.some(isItemVisible))
          .map((group, gIdx) => {
          const visibleItems = group.items.filter(isItemVisible);
          const isOpen = openSections.includes(group.groupName);
          return (
            <div key={gIdx} className="space-y-2">
              {!collapsed && (
                <button
                  onClick={() => toggleSection(group.groupName)}
                  className="w-full flex items-center justify-between px-3 text-sm font-semibold text-slate-600 mb-3 leading-none hover:text-slate-900 transition-all group"
                >
                  {group.groupName}
                  {group.groupName !== "Core" && (
                    <ChevronDown size={10} className={`transform transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-0' : '-rotate-90 opacity-40'}`} />
                  )}
                </button>
              )}

              <ul className={`space-y-1 overflow-hidden transition-all duration-300 ease-in-out ${collapsed ? 'opacity-100 max-h-[1000px]' : isOpen ? 'opacity-100 max-h-[500px]' : 'opacity-0 max-h-0'}`}>
                {visibleItems.map((m) => (
                  <li key={m.to}>
                    <NavLink
                      to={m.to}
                      end={m.to === "/dashboard" || m.to === "/dashboard/treasury"}
                      title={collapsed ? m.name : undefined}
                      className={({ isActive }) =>
                        `flex items-center gap-3 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-xl transition-all duration-200 group border-l-2
                         ${
                           isActive
                             ? "bg-softBlue text-primary border-primary font-medium"
                             : "text-muted hover:bg-surfaceHover hover:text-text border-transparent font-medium"
                         }`
                      }
                    >
                      <div className={`transition-colors duration-200 ${location.pathname === m.to ? 'text-primary' : 'text-slate-400 group-hover:text-text'}`}>
                        {m.icon}
                      </div>
                      {!collapsed && (
                        <span className="text-sm tracking-tight">{m.name}</span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* ───────── Footer ───────── */}
      <div className="px-3 py-4 border-t border-slate-100">
        <div className="flex items-center gap-3 px-1">
          <NavLink
            to="/dashboard/profile"
            title="My Profile"
            className={({ isActive }) =>
              `flex items-center gap-3 flex-1 min-w-0 rounded-xl p-1 transition-colors ${isActive ? "bg-indigo-50" : "hover:bg-slate-50"}`
            }
          >
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm shrink-0">
              <Users size={16} />
            </div>

            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-slate-900 truncate">
                  My Profile
                </div>
                <div className="text-[9px] text-slate-400 font-medium truncate mt-0.5">
                  Account & Settings
                </div>
              </div>
            )}
          </NavLink>

          <button
            onClick={() => onLogout && onLogout()}
            className="p-2 rounded-[14px] text-slate-400 hover:bg-red-50 hover:text-red-600 transition-all"
            title="Logout"
          >
            <LogOut className="w-4 h-4 font-bold" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
